/**
 * useSections — fetches CMS section records for a given page.
 *
 * Behaviour:
 *   • Reads `?preview=<token>` from the current URL. If present, calls
 *     `/api/cms/sections/preview/{token}` to fetch the DRAFT snapshot for the
 *     single section that owns that token. Falls back to published data for
 *     everything else.
 *   • Otherwise fetches `/api/cms/sections/public/{page_slug}` — returns only
 *     currently published + active sections, ordered by `order`.
 *
 * Returns:
 *   sections     Array<{ id, section_id, order, content, ... }>
 *   loading      boolean
 *   error        string | null
 *   getSection   (section_id) => section | null   ← convenience selector
 *
 * Consumers wire this in place of the ad-hoc `usePageContent`/`getSection` pattern
 * as pages migrate to the section-based CMS (Phase B Step 4).
 */
import { useEffect, useMemo, useState } from 'react';
import { fetchWithRetry } from '@/utils/fetchWithRetry';

const API_URL = window.location.origin;

const readPreviewToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('preview');
  } catch {
    return null;
  }
};

export const useSections = (pageSlug) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!pageSlug) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      // 1. Published sections for the page
      const publicRes = await fetchWithRetry(
        `${API_URL}/api/cms/sections/public/${encodeURIComponent(pageSlug)}`,
        {},
        { label: `sections-${pageSlug}` },
      );
      let published = [];
      if (publicRes && publicRes.ok) {
        try {
          const json = await publicRes.json();
          published = Array.isArray(json.sections) ? json.sections : [];
        } catch (err) {
          setError(err.message);
        }
      }

      // 2. Optionally overlay draft content for a single section via preview token
      const token = readPreviewToken();
      if (token) {
        const previewRes = await fetchWithRetry(
          `${API_URL}/api/cms/sections/preview/${encodeURIComponent(token)}`,
          {},
          { label: `sections-preview` },
        );
        if (previewRes && previewRes.ok) {
          try {
            const draft = await previewRes.json();
            if (draft && draft.page_slug === pageSlug && draft.section_id) {
              const idx = published.findIndex((s) => s.section_id === draft.section_id);
              const overlay = {
                id: draft.id,
                section_id: draft.section_id,
                order: draft.order ?? (idx >= 0 ? published[idx].order : 999),
                content: draft.content || {},
                is_preview: true,
              };
              if (idx >= 0) published.splice(idx, 1, overlay);
              else published.push(overlay);
            }
          } catch {
            /* ignore malformed preview; keep published as-is */
          }
        }
      }

      if (!cancelled) {
        setSections(published.sort((a, b) => (a.order || 0) - (b.order || 0)));
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [pageSlug]);

  // Selector: get one section by its stable section_id
  const bySectionId = useMemo(() => {
    const map = {};
    for (const s of sections) map[s.section_id] = s;
    return map;
  }, [sections]);

  const getSection = (sectionId) => bySectionId[sectionId] || null;

  return { sections, loading, error, getSection };
};

export default useSections;
